import React from 'react';
import { SavedChartState, TimeSignature, KeySignature, RecordedMidiNote } from '../types';
import { FeatureKey } from '../utils/features';
import { FreemiumMessageKey, FREEMIUM_LIMITS } from '../utils/freemiumLimits';
import { exportChartToPDFVector, PDFExportOptions } from '../utils/pdfExport';
import * as db from '../utils/db';
import * as drive from '../utils/drive';
import { CURRENT_CHART_SCHEMA_VERSION, migrateChartToCurrent } from '../utils/chartMigrations';
import { formatChordNotatorChord } from '../utils/audio';

interface ChartFields {
  songTitle: string;
  songAuthor: string;
  songStyle: string;
  numberOfMeasures: number;
  timeSignature: TimeSignature;
  keySignature: KeySignature;
  sourceChart: any[];
  transposeAmount: number;
  audioSettings: any;
  uiSettings: any;
  recordedNotes?: RecordedMidiNote[];
}

const normalizeSourceChart = (sourceChart: any[]) => sourceChart.map(measure => ({
  ...measure,
  chords: measure.chords.map((chord: string | null) => chord ? formatChordNotatorChord(chord) : chord),
}));

interface ChartCRUDDeps {
  chartFields: ChartFields;
  setSongTitle: (title: string) => void;
  setSongAuthor: (author: string) => void;
  setSongStyle: (style: string) => void;
  updateSourceChart: (updater: (prev: any[]) => any[]) => void;
  userProfile: any;
  isGapiReady: boolean;
  setSyncStatus: React.Dispatch<React.SetStateAction<'idle' | 'syncing' | 'synced' | 'error'>>;
  fullSync: () => Promise<void>;
  loadSavedChartTitles: () => Promise<void>;
  markSaved: () => void;
  setSaveStatus: React.Dispatch<React.SetStateAction<'idle' | 'saving' | 'saved'>>;
  saveStatusTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
  applyLoadedStateWithPlaybackReset: (parsed: SavedChartState) => void;
  savedChartsData: SavedChartState[];
  subscription: { isPremium: boolean; customerId?: string | null };
  hasFeature: (feature: FeatureKey) => boolean;
  upgradeToPremium: (feature: FeatureKey) => void;
  setFeatureLimitMessageKey: (key: FreemiumMessageKey | undefined) => void;
  setFeatureLimitModalOpen: (open: boolean) => void;
  jamSessionState: string;
  jamSessionRole: string | null;
  setLoadingChartMessage: (msg: string) => void;
  setIsLoadingChart: (loading: boolean) => void;
  modals: {
    openNameRequiredModal: () => void;
    closeSaveAsModal: () => void;
    setSaveAsTitle: (title: string) => void;
    openEmptyChartModal: () => void;
    closeEmptyChartModal: () => void;
    openLoadModal: () => void;
    closeLoadModal: () => void;
    setIsLoadModalRefreshing: (refreshing: boolean) => void;
    openPDFExportModal: () => void;
  };
}

export function useChartCRUD(deps: ChartCRUDDeps) {
  const {
    chartFields,
    setSongTitle, setSongAuthor, setSongStyle,
    updateSourceChart,
    userProfile, isGapiReady, setSyncStatus, fullSync,
    loadSavedChartTitles, markSaved, setSaveStatus, saveStatusTimerRef,
    applyLoadedStateWithPlaybackReset, savedChartsData,
    subscription, hasFeature, upgradeToPremium,
    setFeatureLimitMessageKey, setFeatureLimitModalOpen,
    jamSessionState, jamSessionRole,
    setLoadingChartMessage, setIsLoadingChart,
    modals,
  } = deps;

  const {
    songTitle, songAuthor, songStyle, numberOfMeasures,
    timeSignature, keySignature, sourceChart,
    transposeAmount, audioSettings, uiSettings, recordedNotes,
  } = chartFields;

  const handleSave = async () => {
    if (!songTitle.trim()) {
      modals.openNameRequiredModal();
      return;
    }

    setSaveStatus('saving');

    const stateToSave: SavedChartState = {
      chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
      songTitle,
      songAuthor,
      songStyle,
      numberOfMeasures,
      timeSignature,
      keySignature,
      sourceChart: normalizeSourceChart(sourceChart),
      transposeAmount,
      audioSettings,
      uiSettings,
      recordedNotes,
      lastModified: new Date().toISOString(),
    };

    try {
      await db.saveChart(stateToSave);
      await db.setLastOpenedChartTitle(songTitle);

      if (userProfile && hasFeature('cloudSync')) {
        setSyncStatus('syncing');
        drive.saveChartToDrive(stateToSave)
          .then(() => setSyncStatus('synced'))
          .catch(error => {
            console.error("Background sync failed on save:", error);
            setSyncStatus('error');
          });
      } else if (userProfile && !hasFeature('cloudSync')) {
        console.log('Cloud sync requires premium subscription');
      }

      await loadSavedChartTitles();
      markSaved();
      setSaveStatus('saved');

      if (saveStatusTimerRef.current) {
        clearTimeout(saveStatusTimerRef.current);
      }
      saveStatusTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch (error) {
      console.error("Failed to save chart locally:", error);
      setSaveStatus('idle');
      alert("There was a critical error saving your chart to the local database.");
    }
  };

  const handleSaveAs = async (newTitle: string) => {
    if (!newTitle.trim()) {
      alert("Please enter a valid chart title.");
      return;
    }

    const existingChart = await db.getChart(newTitle);
    if (existingChart) {
      const confirmOverwrite = confirm(`A chart named "${newTitle}" already exists. Do you want to overwrite it?`);
      if (!confirmOverwrite) {
        return;
      }
    }

    const stateToSave: SavedChartState = {
      chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
      songTitle: newTitle,
      songAuthor,
      songStyle,
      numberOfMeasures,
      timeSignature,
      keySignature,
      sourceChart: normalizeSourceChart(sourceChart),
      transposeAmount,
      audioSettings,
      uiSettings,
      recordedNotes,
      lastModified: new Date().toISOString(),
    };

    try {
      await db.saveChart(stateToSave);
      await db.setLastOpenedChartTitle(newTitle);
      setSongTitle(newTitle);

      if (userProfile) {
        setSyncStatus('syncing');
        drive.saveChartToDrive(stateToSave)
          .then(() => setSyncStatus('synced'))
          .catch(error => {
            console.error("Background sync failed on save as:", error);
            setSyncStatus('error');
          });
      }

      await loadSavedChartTitles();
      modals.closeSaveAsModal();
      modals.setSaveAsTitle('');

      setTimeout(() => {
        console.log('Chart saved as:', newTitle);
      }, 300);
    } catch (error) {
      console.error("Failed to save chart as:", error);
      alert("There was an error saving your chart.");
    }
  };

  const handleEmptyChart = () => {
    if (!subscription.isPremium) {
      const chartCount = savedChartsData.length;
      if (chartCount >= FREEMIUM_LIMITS.FREE_MAX_CHARTS) {
        setFeatureLimitMessageKey('CHARTS_LIMIT_REACHED');
        setFeatureLimitModalOpen(true);
        return;
      }
    }
    modals.openEmptyChartModal();
  };

  const confirmEmptyChart = () => {
    setSongTitle('');
    setSongAuthor('');
    setSongStyle('Swing');

    updateSourceChart(prevChart => {
      return prevChart.map(measure => ({
        ...measure,
        chords: measure.chords.map(() => ''),
        annotations: {},
        lyrics: '',
      }));
    });
    modals.closeEmptyChartModal();
  };

  const handleLoadChart = async (titleToLoad: string, _isFromNavigation = false) => {
    if (jamSessionState !== 'idle' && jamSessionRole === 'client') {
      alert('Cannot load charts while connected to a jam session as a client. Only the host can change charts.');
      return;
    }

    setLoadingChartMessage(`Loading "${titleToLoad}"...`);
    setIsLoadingChart(true);

    try {
      const savedData = await db.getChart(titleToLoad);
      if (savedData) {
        applyLoadedStateWithPlaybackReset(savedData);
        await db.setLastOpenedChartTitle(titleToLoad);
        modals.closeLoadModal();
      } else {
        alert(`Could not find chart titled "${titleToLoad}".`);
        await loadSavedChartTitles();
      }
    } catch (error) {
      console.error("Failed to load chart:", error);
      alert("There was an error loading the chart.");
    } finally {
      setIsLoadingChart(false);
    }
  };

  const handleDeleteChart = async (titleToDelete: string) => {
    if (window.confirm(`Are you sure you want to permanently delete "${titleToDelete}"?`)) {
      try {
        await db.deleteChart(titleToDelete);
        if (userProfile) {
          await drive.deleteChartFromDrive(titleToDelete);
        } else {
          await db.addPendingDelete(titleToDelete);
        }
        await loadSavedChartTitles();
      } catch (error) {
        console.error("Failed to delete chart:", error);
        alert("There was an error deleting the chart.");
      }
    }
  };

  const handleRefreshCharts = async () => {
    if (!userProfile || !isGapiReady) return;

    modals.setIsLoadModalRefreshing(true);
    try {
      await fullSync();
      await loadSavedChartTitles();
    } catch (error) {
      console.error("Failed to refresh charts:", error);
    } finally {
      modals.setIsLoadModalRefreshing(false);
    }
  };

  const handleExportPDF = async () => {
    if (!hasFeature('pdfExport')) {
      console.log('PDF export requires premium');
      upgradeToPremium('pdfExport');
      return;
    }

    if (!songTitle.trim()) {
      alert("Please add a song title before exporting to PDF.");
      return;
    }

    modals.openPDFExportModal();
  };

  const handlePDFExportWithOptions = async (options: PDFExportOptions) => {
    try {
      const chartToExport: SavedChartState = {
        chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
        songTitle,
        songAuthor,
        songStyle,
        numberOfMeasures,
        timeSignature,
        keySignature,
        sourceChart: normalizeSourceChart(sourceChart),
        transposeAmount,
        audioSettings,
        uiSettings,
        lastModified: new Date().toISOString(),
      };

      await exportChartToPDFVector(chartToExport, options);
      console.log('Vector PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const handleExportJSON = () => {
    if (!songTitle.trim()) {
      alert("Please add a song title before exporting.");
      return;
    }
    const chartToExport: SavedChartState = {
      chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
      songTitle, songAuthor, songStyle, numberOfMeasures,
      timeSignature, keySignature, sourceChart: normalizeSourceChart(sourceChart),
      transposeAmount, audioSettings, uiSettings,
      lastModified: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(chartToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${songTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    console.log('Chart exported as JSON:', songTitle);
  };

  const handleImportJSON = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text) as SavedChartState;
        if (!parsed.songTitle || !Array.isArray(parsed.sourceChart) || typeof parsed.numberOfMeasures !== 'number') {
          alert('Invalid chart file. Please select a valid Chord Notator JSON file.');
          return;
        }
        const migrated = migrateChartToCurrent(parsed);
        const existing = await db.getChart(migrated.songTitle);
        if (existing) {
          const overwrite = confirm(`A chart named "${migrated.songTitle}" already exists. Overwrite it?`);
          if (!overwrite) return;
        }
        await db.saveChart(migrated);
        await loadSavedChartTitles();
        applyLoadedStateWithPlaybackReset(migrated);
        console.log('Chart imported:', migrated.songTitle);
      } catch {
        alert('Failed to read the file. Make sure it is a valid JSON chart file.');
      }
    };
    input.click();
  };

  return {
    handleSave,
    handleSaveAs,
    handleEmptyChart,
    confirmEmptyChart,
    handleLoadChart,
    handleDeleteChart,
    handleRefreshCharts,
    handleExportPDF,
    handlePDFExportWithOptions,
    handleExportJSON,
    handleImportJSON,
  };
}

export type UseChartCRUDReturn = ReturnType<typeof useChartCRUD>;
